import { DownloadOutlined, FilterOutlined } from "@ant-design/icons";
import AdminPage from "admin/admin_page";
import { getTeams, getTimeEntries, getTimeTrackingForUserSpans } from "admin/rest_api";
import { Button, DatePicker, Select, Space, Spin, Table, type TimeRangePickerProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import FixedExpandableTable from "components/fixed_expandable_table";
import LinkButton from "components/link_button";
import dayjs, { type Dayjs } from "dayjs";
import { formatMilliseconds } from "libs/format_utils";
import { useQueryWithErrorHandling, useWkSelector } from "libs/react_hooks";
import Toast from "libs/toast";
import { compareBy, isUserAdminOrTeamManager, localeCompareBy } from "libs/utils";
import messages from "messages";
import { useState } from "react";
import type { APITimeTrackingPerUser } from "types/api_types";
import { AnnotationStateFilterEnum, AnnotationTypeFilterEnum } from "viewer/constants";
import { saveAsCSV, transformToCSVRow } from "viewer/model/helpers/csv_helpers";
import ProjectAndAnnotationTypeDropdown from "./project_and_annotation_type_dropdown";
import TimeTrackingDetailView from "./time_tracking_detail_view";

const { RangePicker } = DatePicker;

const TIMETRACKING_CSV_HEADER_PER_USER = ["userId,userFirstName,userLastName,timeTrackedInSeconds"];
const TIMETRACKING_CSV_HEADER_SPANS = [
  "userId,email,datasetOrga,datasetName,annotation,annotationState,startTimeUnixTimestamp,durationInSeconds,taskId,projectName,taskTypeId,taskTypeSummary",
];

function TimeTrackingOverview() {
  const currentTime = dayjs();
  const [startDate, setStartDate] = useState(currentTime.startOf("month"));
  const [endDate, setEndDate] = useState(currentTime);
  const isCurrentUserAdminOrManager = useWkSelector((state) => {
    const activeUser = state.activeUser;
    return activeUser != null && isUserAdminOrTeamManager(activeUser);
  });

  const { data: allTeams = [], isLoading: isTeamsLoading } = useQueryWithErrorHandling({
    queryKey: ["teams"],
    queryFn: getTeams,
  });

  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState(
    AnnotationTypeFilterEnum.TASKS_AND_ANNOTATIONS_KEY,
  );
  const [selectedState, setSelectedState] = useState(AnnotationStateFilterEnum.ALL);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);

  const { data: filteredTimeEntries = [], isFetching: isEntriesFetching } =
    useQueryWithErrorHandling({
      queryKey: [
        "timeEntries",
        selectedTeams,
        selectedTypes,
        selectedState,
        selectedProjectIds,
        startDate.valueOf(),
        endDate.valueOf(),
      ],
      queryFn: () =>
        getTimeEntries(
          startDate.valueOf(),
          endDate.valueOf(),
          selectedTeams,
          selectedTypes,
          selectedState,
          selectedProjectIds,
        ),
    });

  const isFetching = isTeamsLoading || isEntriesFetching;

  const downloadTimeSpans = async (
    userId: string,
    start: Dayjs,
    end: Dayjs,
    annotationTypes: AnnotationTypeFilterEnum,
    selectedState: AnnotationStateFilterEnum,
    projectIds: string[] | null | undefined,
  ) => {
    const timeSpans = await getTimeTrackingForUserSpans(
      userId,
      start.valueOf(),
      end.valueOf(),
      annotationTypes,
      selectedState,
      projectIds,
    );
    const timeEntriesAsString = timeSpans.map((row) =>
      transformToCSVRow([
        row.userId,
        row.userEmail,
        row.datasetOrganization,
        row.datasetName,
        row.annotationId,
        row.annotationState,
        row.timeSpanCreated,
        Math.ceil(row.timeSpanTimeMillis / 1000),
        row.taskId,
        row.projectName,
        row.taskTypeId,
        row.taskTypeSummary,
      ]),
    );
    const filename = `timetracking-user-export-${userId}.csv`;
    saveAsCSV(TIMETRACKING_CSV_HEADER_SPANS, timeEntriesAsString, filename);
  };

  const exportToCSV = () => {
    if (filteredTimeEntries.length === 0) {
      return;
    }
    const timeEntries = filteredTimeEntries.map((row) => {
      return transformToCSVRow([
        row.user.id,
        row.user.firstName,
        row.user.lastName,
        Math.round(row.timeMillis / 1000),
      ]);
    });
    saveAsCSV(TIMETRACKING_CSV_HEADER_PER_USER, timeEntries, "timetracking-export.csv");
  };

  const rangePresets: TimeRangePickerProps["presets"] = [
    { label: "Last 7 Days", value: [dayjs().subtract(7, "d").startOf("day"), currentTime] },
    { label: "Last 30 Days", value: [dayjs().subtract(30, "d").startOf("day"), currentTime] },
  ];

  const renderPlaceholder = () => {
    return (
      <p>
        There is no user activity recorded for the selected time span. Please adjust the time range
        and filters above.
      </p>
    );
  };

  const timeTrackingTableColumns: ColumnsType<APITimeTrackingPerUser> = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (user: APITimeTrackingPerUser["user"]) =>
        `${user.lastName}, ${user.firstName} (${user.email})`,
      sorter: localeCompareBy<APITimeTrackingPerUser>(
        (timeEntry) =>
          `${timeEntry.user.lastName}, ${timeEntry.user.firstName} (${timeEntry.user.email})`,
      ),
    },
    {
      title: "No. tasks / annotations",
      dataIndex: "annotationCount",
      align: "right",
      key: "numberAnn",
      sorter: compareBy<APITimeTrackingPerUser>((timeEntry) => timeEntry.annotationCount),
    },
    {
      title: "Avg. time per task / annotation",
      key: "avgTime",
      align: "right",
      render: (item: APITimeTrackingPerUser) =>
        formatMilliseconds(item.timeMillis / item.annotationCount),
      sorter: compareBy<APITimeTrackingPerUser>(
        (timeEntry) => timeEntry.timeMillis / timeEntry.annotationCount,
      ),
    },
    {
      title: "Total time",
      dataIndex: "timeMillis",
      key: "tracingTimes",
      align: "right",
      render: (tracingTimeInMs: APITimeTrackingPerUser["timeMillis"]) =>
        formatMilliseconds(tracingTimeInMs),
      sorter: compareBy<APITimeTrackingPerUser>((timeEntry) => timeEntry.timeMillis),
    },
    {
      key: "details",
      dataIndex: "user",
      render: (user: APITimeTrackingPerUser["user"]) => {
        return (
          <LinkButton
            onClick={async () => {
              downloadTimeSpans(
                user.id,
                startDate,
                endDate,
                selectedTypes,
                selectedState,
                selectedProjectIds,
              );
            }}
            icon={<DownloadOutlined />}
          >
            Download time spans
          </LinkButton>
        );
      },
    },
  ];

  const getSummaryRow = (pageData: readonly APITimeTrackingPerUser[]) => {
    if (pageData.length === 0) {
      return null;
    }

    let totalNumberOfTasksAndAnnotations = 0;
    let totalTimeMs = 0;
    pageData.forEach(({ timeMillis, annotationCount }) => {
      totalNumberOfTasksAndAnnotations += annotationCount;
      totalTimeMs += timeMillis;
    });

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} />
        <Table.Summary.Cell index={1}>
          <b>Total</b>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2} align="right">
          {totalNumberOfTasksAndAnnotations}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3} align="right">
          {formatMilliseconds(totalTimeMs / totalNumberOfTasksAndAnnotations)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="right">
          {formatMilliseconds(totalTimeMs)}
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5} />
      </Table.Summary.Row>
    );
  };

  return (
    <AdminPage
      title="Annotation Time per User"
      descriptionURI="https://docs.webknossos.org/webknossos/tasks_projects/tasks.html"
      description="Analyze time spent on annotations and tasks by project, annotation type, state, team, and date range."
      actions={
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => exportToCSV()}
          disabled={filteredTimeEntries.length === 0}
        >
          Export to CSV
        </Button>
      }
      filters={
        <Space>
          <ProjectAndAnnotationTypeDropdown
            setSelectedProjectIds={setSelectedProjectIds}
            selectedProjectIds={selectedProjectIds}
            setSelectedAnnotationType={setSelectedTypes}
            selectedAnnotationType={selectedTypes}
            selectedAnnotationState={selectedState}
            setSelectedAnnotationState={setSelectedState}
          />
          <Select
            mode="multiple"
            placeholder="Filter teams"
            defaultValue={[]}
            disabled={!isCurrentUserAdminOrManager}
            style={{ width: 400 }}
            options={allTeams.map((team) => {
              return {
                label: team.name,
                value: team.id,
              };
            })}
            value={selectedTeams}
            onSelect={(teamIdOrKey: string) => setSelectedTeams([...selectedTeams, teamIdOrKey])}
            onDeselect={(removedTeamId: string) => {
              setSelectedTeams(selectedTeams.filter((teamId) => teamId !== removedTeamId));
            }}
            prefix={<FilterOutlined />}
          />
          <RangePicker
            value={[startDate, endDate]}
            presets={rangePresets}
            onChange={(dates: [Dayjs | null, Dayjs | null] | null) => {
              if (dates == null || dates[0] == null || dates[1] == null) return;
              if (Math.abs(dates[0].diff(dates[1], "days")) > 3 * 31) {
                Toast.error(messages["timetracking.date_range_too_long"]);
                return;
              }
              setStartDate(dates[0].startOf("day"));
              setEndDate(dates[1].endOf("day"));
            }}
          />
        </Space>
      }
    >
      <Spin spinning={isFetching} size="large">
        <FixedExpandableTable
          dataSource={filteredTimeEntries}
          rowKey="user"
          pagination={false}
          columns={timeTrackingTableColumns}
          expandable={{
            expandedRowRender: (entry) => (
              <TimeTrackingDetailView
                userId={entry.user.id}
                dateRange={[startDate.valueOf(), endDate.valueOf()]}
                annotationType={selectedTypes}
                annotationState={selectedState}
                projectIds={selectedProjectIds}
              />
            ),
          }}
          locale={{
            emptyText: renderPlaceholder(),
          }}
          summary={getSummaryRow}
        />
      </Spin>
    </AdminPage>
  );
}

export default TimeTrackingOverview;
